/* eslint-disable no-console */
/* eslint-disable sonarjs/no-identical-functions */
/* eslint-disable sonarjs/cognitive-complexity */
import React, { useState, useEffect, useRef } from 'react';
import {
  Grid,
  Card,
  Typography,
  RACTable,
  RACTableCell,
  RACTableRow,
  RACButton,
  RACCheckBox,
  CircularProgress,
  RACModalCard,
  RACTextbox,
} from '@rentacenter/racstrap';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { customerStyles } from '../customerStyles';
import SearchBox from './search';
import LoadingSpinner from './LoadingSpinner';
import NoRecordsFound from './NoRecords';
import { getIncidents } from '../../api/user';
import { PAGESIZE } from '../../constants/constants';
import { useStoreDetails } from '../../context/Store/StoreProvider';
import {
  buildIncidentParamsNested,
  initialSearch,
  SearchState,
  updateSearchState,
} from '../../utils/utils';
import { SearchBoxHandle } from '../../types/types';

type Props = {
  statuses?: string[];
  pageType: string;
  openedBy?: string;
  role?: string;
  email?: string;
};

export default function SearchResult({
  statuses = [],
  openedBy,
  pageType,
  role,
  email,
}: Props) {
  const classes = customerStyles();
  const history = useHistory();
  const { reportAccessRoles, loader } = useStoreDetails();
  const searchBoxRef = useRef<SearchBoxHandle | null>(null);

  const [search, setSearch] = useState<SearchState>(initialSearch);
  const [incidents, setIncidents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [hasMore, setHasMore] = useState(true);
  const [offSet, setOffSet] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAllLoaded, setSelectAllLoaded] = useState<boolean>(false);
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());
  const [searchClicked, setSearchClicked] = useState(false);
  const [submittedSearchValue, setSubmittedSearchValue] = useState('');
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [additionalEmail, setAdditionalEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(false);

  const limit = PAGESIZE;
  const anySearchValue = submittedSearchValue;

  const getRowId = (t: any, idx?: number) =>
    String(t?.csmTicketNumber ?? t?.id ?? idx);

  const hasAccess = !!role && reportAccessRoles.includes(role ?? '');

  useEffect(() => {
    fetchIncidents(true, search.searchValue, 0, openedBy, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetSelection = () => {
    setSelectAllLoaded(false);
    setSelectedIds(new Set());
    setExcludedIds(new Set());
  };

  const clearEverythingAndReload = () => {
    setSearch(initialSearch);
    setSearchClicked(false);
    setSubmittedSearchValue('');
    setOffSet(0);
    resetSelection();
    setIncidents([]);
    setTotalResults(0);
    fetchIncidents(true, '', 0, undefined, initialSearch);
  };

  const buildExportEmail = (): string => {
    const primary = (email ?? '').trim();
    const additional = (additionalEmail ?? '').trim();
    return additional ? additional : primary;
  };

  const fetchIncidents = async (
    reset = false,
    overrideSearchValue?: string,
    currentOffSet?: number,
    openedByParam?: string,
    searchOverride?: SearchState
  ) => {
    try {
      const isReset = reset;
      const effectiveSearch = searchOverride ?? search;
      const searchValue = overrideSearchValue ?? submittedSearchValue;
      const apiOffset = currentOffSet ?? offSet;
      const pageNumber = Math.floor(apiOffset / limit) + 1;

      if (isReset) {
        setInitialLoading(true);
        setIncidents([]);
        setOffSet(0);
        setHasMore(true);
        setError(null);
      } else {
        setLoading(true);
      }

      const baseParams: Record<string, any> = {
        pageNumber: pageNumber.toString(),
        pageSize: limit.toString(),
        statuses,
        searchValue,
        ...(openedByParam && { openedBy: openedByParam }),
      };

      const params = buildIncidentParamsNested(baseParams, effectiveSearch);

      const res = await getIncidents(params);

      if (!res || !Array.isArray(res.tickets)) {
        if (isReset) setIncidents([]);
        setTotalResults(0);
        setHasMore(false);
        return;
      }

      const newTickets = res.tickets ?? [];
      const newTotalResults = Number(res.totalResults ?? 0);

      setIncidents((prev) => (isReset ? newTickets : [...prev, ...newTickets]));
      setTotalResults(newTotalResults);

      const loadedCount = (apiOffset ?? 0) + newTickets.length;
      const more = newTickets.length === limit && loadedCount < newTotalResults;
      setHasMore(more);
    } catch (err: any) {
      setError(err.message ?? 'Unknown error');
      setIncidents([]);
      setTotalResults(0);
      setHasMore(false);
    } finally {
      setInitialLoading(false);
      setLoading(false);
    }
  };

  const exportIncidentsByEmail = async () => {
    const exportEmail = buildExportEmail();
    if (!exportEmail) return;

    const baseParams: Record<string, any> = {
      pageNumber: '1',
      pageSize: '1',
      statuses,
      searchValue: search.searchValue,
      ...(search.openedBy && { openedBy: search.openedBy }),
    };

    const params = buildIncidentParamsNested(baseParams, search, {
      exportEmail,
      export: 'Y',
    });

    await getIncidents(params);
  };

  const formatDateTime = (isoString?: string) => {
    if (!isoString) return ' ';
    const dateObj = new Date(isoString);
    if (isNaN(dateObj.getTime())) return ' ';
    const date = dateObj.toISOString().split('T')[0];
    const pad = (num: number) => num.toString().padStart(2, '0');
    const hours = pad(dateObj.getHours());
    const minutes = pad(dateObj.getMinutes());
    const seconds = pad(dateObj.getSeconds());
    return (
      <>
        {date}
        <br />
        {`${hours}:${minutes}:${seconds}`}
      </>
    );
  };

  const formatBusinessDuration = (duration: string) => {
    if (!duration) return '';
    const parts = duration.split(':');
    const days = parseInt(parts[0], 10);
    const hours = parseInt(parts[1], 10);
    const minutes = parseInt(parts[2], 10);
    const seconds = parseInt(parts[3], 10);

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ${hours} hour${
        hours !== 1 ? 's' : ''
      }`.trim();
    } else if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${
        minutes !== 1 ? 's' : ''
      }`.trim();
    } else if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ${seconds} second${
        seconds !== 1 ? 's' : ''
      }`.trim();
    } else if (seconds > 0) {
      return `${seconds} second${seconds !== 1 ? 's' : ''}`.trim();
    } else {
      return '';
    }
  };

  const handleSearchChange = (name: string, value: string | string[]) => {
    setSearch((prev: any) => updateSearchState(prev, name, value));
  };

  const handleSearchSubmit = (criteria: SearchState) => {
    setSearch(criteria);
    setSearchClicked(true);
    setSubmittedSearchValue(criteria.searchValue ?? '');
    setOffSet(0);
    resetSelection();
    fetchIncidents(true, criteria.searchValue, 0, criteria.openedBy, criteria);
  };

  const handleClearClick = () => {
    clearEverythingAndReload();
  };

  const handleFilterModeChange = () => {
    clearEverythingAndReload();
  };

  const fetchNextData = () => {
    if (!loading && hasMore) {
      const nextOffset = offSet + limit;
      setOffSet(nextOffset);
      fetchIncidents(
        false,
        submittedSearchValue ?? search.searchValue,
        nextOffset,
        search.openedBy
      );
    }
  };

  const loadedIds: string[] = incidents.map(getRowId);

  const allLoadedSelected =
    loadedIds.length > 0 &&
    (selectAllLoaded
      ? excludedIds.size === 0
      : loadedIds.every((id) => selectedIds.has(id)));

  const someLoadedSelected =
    loadedIds.length > 0 &&
    !allLoadedSelected &&
    (selectAllLoaded
      ? excludedIds.size > 0 && excludedIds.size < loadedIds.length
      : selectedIds.size > 0);

  const toggleRow = (id: string) => {
    if (selectAllLoaded) {
      setExcludedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }
  };

  const handleHeaderCheckboxChange = () => {
    if (allLoadedSelected) {
      setSelectAllLoaded(false);
      setSelectedIds(new Set());
      setExcludedIds(new Set());
      return;
    }
    setSelectAllLoaded(true);
    setExcludedIds(new Set());
    setSelectedIds(new Set());
  };

  const selectedCount = selectAllLoaded
    ? Math.max(0, incidents.length - excludedIds.size)
    : selectedIds.size;

  const displayValue = (value: any) => {
    return (
      <div
        style={{
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          maxWidth: '150px',
        }}
      >
        {value ?? ' '}
      </div>
    );
  };

  const handleExportSelected = () => {
    const itemsToExport = selectAllLoaded
      ? incidents.filter((t, i) => !excludedIds.has(getRowId(t, i)))
      : incidents.filter((t, i) => selectedIds.has(getRowId(t, i)));

    if (!itemsToExport.length) return;

    const exportRows = itemsToExport.map((ticket) => ({
      'Incident Number': ticket.csmTicketNumber ?? '',
      Status: ticket.status ?? '',
      'Escalated to': ticket.escalateToRole ?? '',
      'Assignment Group': ticket.assignmentGroupDescEn ?? '',
      'SN Number': ticket.snTicketNumber ?? '',
      'First Name': ticket.contactInfo?.firstName ?? '',
      'Last Name': ticket.contactInfo?.lastName ?? '',
      'Call-Back Number': ticket.contactInfo?.primaryPhoneNumber ?? '',
      'Additional Number': ticket.contactInfo?.alternatePhoneNumber ?? '',
      Channel: ticket.channelDescEn ?? '',
      Category: ticket.categoryDescEn ?? '',
      Subcategory: ticket.subCategoryDescEn ?? '',
      Action: ticket.actionTypeDescEn ?? '',
      'Short Description': ticket.shortDescription ?? '',
      Description: ticket.description ?? '',
      'Rental Agreement Number': ticket.rentalReferenceNumber ?? '',
      'Store Number': ticket.storeNumber ?? '',
      Created: ticket.createdDate ?? '',
      Updated: ticket.lastModifiedDate ?? '',
      Resolved: ticket.resolvedDate ?? '',
      'Business Duration': ticket.businessDuration ?? '',
      'Opened by': ticket.openedByName ?? '',
      'Requestor Type': ticket.requestTypeDescEn ?? '',
      'Created by': ticket.createdBy ?? '',
      'Resolved by': ticket.resolvedBy ?? '',
      Language: ticket.languageDescEn ?? '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Incidents');
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([wbout], { type: 'application/octet-stream' }),
      'exported_incidents.xlsx'
    );
  };

  const handleExportEmailClose = () => {
    setEmailModalOpen(false);
  };

  const handleSendReport = async () => {
    try {
      setSendingEmail(true);
      await exportIncidentsByEmail();
      handleExportEmailClose();
      setAdditionalEmail('');
    } catch (e) {
      console.error('Email export failed:', e);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCreateNewIncident = () => {
    history.push('/csm/new');
  };

  const EmailValueVerify = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setAdditionalEmail(value);
    const EmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = value.trim() === '' ? false : EmailRegex.test(value);
    setIsValidEmail(isValid);
  };

  const renderTableHead = () => (
    <>
      <RACTableCell
        className={`${classes.stickyCheckbox} ${classes.stickyNumbers} ${classes.space}`}
      >
        <RACCheckBox
          label=""
          size="small"
          className={classes.customerCheckbox}
          checked={allLoadedSelected}
          indeterminate={someLoadedSelected}
          onChange={handleHeaderCheckboxChange}
        />
      </RACTableCell>
      <RACTableCell className={`${classes.stickyNumbers} ${classes.space}`}>
        Number
      </RACTableCell>
      <RACTableCell className={classes.space}>Status</RACTableCell>
      <RACTableCell className={classes.space}>Escalated to</RACTableCell>
      <RACTableCell className={classes.space}>Assignment Group</RACTableCell>
      <RACTableCell className={classes.space}>SN Number</RACTableCell>
      <RACTableCell className={classes.space}>First Name</RACTableCell>
      <RACTableCell className={classes.space}>Last Name</RACTableCell>
      <RACTableCell className={classes.space}>Call-Back number</RACTableCell>
      <RACTableCell className={classes.space}>Additional number</RACTableCell>
      <RACTableCell className={classes.space}>Channel</RACTableCell>
      <RACTableCell className={classes.space}>Category</RACTableCell>
      <RACTableCell className={classes.space}>Subcategory</RACTableCell>
      <RACTableCell className={classes.space}>Action</RACTableCell>
      <RACTableCell className={classes.space}>Short Description</RACTableCell>
      <RACTableCell className={classes.space}>Description</RACTableCell>
      <RACTableCell className={classes.space}>
        Rental Agreement Number
      </RACTableCell>
      <RACTableCell className={classes.space}>Store Number</RACTableCell>
      <RACTableCell className={classes.space}>Created</RACTableCell>
      <RACTableCell className={classes.space}>Updated</RACTableCell>
      <RACTableCell className={classes.space}>Resolved</RACTableCell>
      <RACTableCell className={classes.space}>Business Duration</RACTableCell>
      <RACTableCell className={classes.space}>Opened by</RACTableCell>
      <RACTableCell className={classes.space}>Requestor Type</RACTableCell>
      <RACTableCell className={classes.space}>Created by</RACTableCell>
      <RACTableCell className={classes.space}>Resolved by</RACTableCell>
      <RACTableCell className={classes.space}>Language</RACTableCell>
    </>
  );

  const renderTableContent = () =>
    incidents.map((ticket, idx) => {
      const id = getRowId(ticket, idx);
      const isChecked = selectAllLoaded
        ? !excludedIds.has(id)
        : selectedIds.has(id);

      return (
        <RACTableRow
          key={`${
            ticket.contactInfo?.globalCustomerId ?? 'ticket'
          }-${id}-${idx}`}
          style={{ backgroundColor: idx % 2 !== 0 ? '#f1efefff' : '#ffffff' }}
        >
          <RACTableCell
            className={`${classes.stickyCheckbox}`}
            style={{ backgroundColor: idx % 2 !== 0 ? '#f1efefff' : '#ffffff' }}
          >
            <RACCheckBox
              label=""
              size="small"
              className={classes.customerCheckbox}
              name={`checkbox_${id}`}
              onChange={() => toggleRow(id)}
              checked={isChecked}
            />
          </RACTableCell>

          <RACTableCell
            className={`${classes.stickyNumber}`}
            style={{ backgroundColor: idx % 2 !== 0 ? '#f1efefff' : '#ffffff' }}
          >
            <a
              onClick={() =>
                history.push(`/csm/info/${ticket.csmTicketNumber}`)
              }
              className={classes.customerRaclink}
            >
              {displayValue(ticket.csmTicketNumber)}
            </a>
          </RACTableCell>

          <RACTableCell>{displayValue(ticket.statusDescEn)}</RACTableCell>
          <RACTableCell>{displayValue(ticket.escalateToRole)}</RACTableCell>
          <RACTableCell>
            {displayValue(ticket.assignmentGroupDescEn)}
          </RACTableCell>
          <RACTableCell>{displayValue(ticket.snTicketNumber)}</RACTableCell>
          <RACTableCell>
            {displayValue(ticket.contactInfo?.firstName)}
          </RACTableCell>
          <RACTableCell>
            {displayValue(ticket.contactInfo?.lastName)}
          </RACTableCell>
          <RACTableCell>
            {displayValue(ticket.contactInfo?.primaryPhoneNumber)}
          </RACTableCell>
          <RACTableCell>
            {displayValue(ticket.contactInfo?.alternatePhoneNumber)}
          </RACTableCell>
          <RACTableCell>{displayValue(ticket?.channelDescEn)}</RACTableCell>
          <RACTableCell>{displayValue(ticket.categoryDescEn)}</RACTableCell>
          <RACTableCell>{displayValue(ticket.subCategoryDescEn)}</RACTableCell>
          <RACTableCell>{displayValue(ticket.actionTypeDescEn)}</RACTableCell>
          <RACTableCell>{displayValue(ticket.shortDescription)}</RACTableCell>
          <RACTableCell>{displayValue(ticket.description)}</RACTableCell>
          <RACTableCell>
            {displayValue(ticket.rentalReferenceNumber)}
          </RACTableCell>
          <RACTableCell>{displayValue(ticket.storeNumber)}</RACTableCell>
          <RACTableCell>
            {displayValue(formatDateTime(ticket.createdDate))}
          </RACTableCell>
          <RACTableCell>
            {displayValue(formatDateTime(ticket.lastModifiedDate))}
          </RACTableCell>
          <RACTableCell>
            {displayValue(formatDateTime(ticket.resolvedDate))}
          </RACTableCell>
          <RACTableCell style={{ width: '150px', whiteSpace: 'nowrap' }}>
            {formatBusinessDuration(ticket.businessDuration)}
          </RACTableCell>
          <RACTableCell>{displayValue(ticket.openedByName)}</RACTableCell>
          <RACTableCell>{displayValue(ticket.requestTypeDescEn)}</RACTableCell>
          <RACTableCell>{displayValue(ticket.createdBy)}</RACTableCell>
          <RACTableCell>{displayValue(ticket.resolvedBy)}</RACTableCell>
          <RACTableCell>{displayValue(ticket.languageDescEn)}</RACTableCell>
        </RACTableRow>
      );
    });

  const panelOpen = searchBoxRef.current?.isFilterPanelOpen?.() ?? false;
  const isExportDisabled = hasAccess
    ? !panelOpen && selectedCount < 1
    : selectedCount < 1;

  return (
    <div className={classes.racFixedFooterMargin}>
      {loader ? (
        <Grid className={classes.GridLoader}>
          <CircularProgress />
        </Grid>
      ) : (
        <SearchBox
          ref={searchBoxRef}
          search={search}
          pageType={pageType}
          hasAccess={hasAccess}
          onSearchChange={handleSearchChange}
          onSubmit={handleSearchSubmit}
          onClearClick={handleClearClick}
          onFilterModeChange={handleFilterModeChange as any}
        />
      )}

      {emailModalOpen && (
        <RACModalCard
          isOpen={emailModalOpen}
          maxWidth="sm"
          titleVariant="h4"
          title="Send Report to Email"
          closeIcon={true}
          onClose={handleExportEmailClose}
          borderRadius="20px"
          classes={{
            dialogContent: classes.dialogContent,
            dialog: classes.dialogRoot,
          }}
        >
          <Grid item md={12} className={classes.modalWrapper}>
            <Typography variant="body2">
              Get your Incident report in the email
            </Typography>
            <Grid container spacing={2} className={classes.formGrid}>
              <Grid item xs={12}>
                <RACTextbox
                  inputlabel={'Email'}
                  isCurrency={false}
                  type="email"
                  value={email || ''}
                  disabled={true}
                  className={classes.searchBoxStyle}
                />
              </Grid>

              <Grid item xs={12}>
                <RACTextbox
                  inputlabel={'Alternate Email'}
                  isCurrency={false}
                  type="email"
                  maxlength={50}
                  value={additionalEmail}
                  OnChange={EmailValueVerify}
                  className={classes.searchBoxStyle}
                  errorMessage={
                    additionalEmail && !isValidEmail
                      ? 'Please enter a valid email address'
                      : ''
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <RACButton
                  type="button"
                  variant="contained"
                  color="primary"
                  className={classes.submitBtn}
                  fullWidth
                  onClick={handleSendReport}
                  disabled={
                    sendingEmail || (!!additionalEmail && !isValidEmail)
                  }
                >
                  {sendingEmail ? 'Sending…' : 'Send Report to Email'}
                </RACButton>
              </Grid>
            </Grid>
          </Grid>
        </RACModalCard>
      )}

      <Grid container item md={12} alignItems="center" style={{ padding: 10 }}>
        <Grid item lg={4}>
          {pageType === 'Search' ? (
            searchClicked && anySearchValue ? (
              <Typography
                variant="h6"
                style={{ marginBottom: 16, fontWeight: 600 }}
              >
                {`Search results for "${submittedSearchValue}"`}
              </Typography>
            ) : (
              <Typography
                variant="h6"
                style={{ marginBottom: 16, fontWeight: 600 }}
              >
                Customer Incidents
              </Typography>
            )
          ) : (
            <Typography
              variant="h6"
              style={{ marginBottom: 16, fontWeight: 600 }}
            >
              Customer Incidents
            </Typography>
          )}
        </Grid>

        <Grid
          item
          lg={8}
          style={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <RACButton
            variant="contained"
            color="primary"
            onClick={handleCreateNewIncident}
          >
            Create New Incident
          </RACButton>
        </Grid>
      </Grid>

      <Grid container className={`${classes.px2} ${classes.mb3}`}>
        <Grid item md={12}>
          <Card className={classes.card}>
            {initialLoading ? (
              <LoadingSpinner message="Loading incidents..." />
            ) : error ? (
              <NoRecordsFound message={`Error loading incidents`} />
            ) : totalResults === 0 || incidents.length === 0 ? (
              <NoRecordsFound />
            ) : (
              <InfiniteScroll
                next={fetchNextData}
                dataLength={incidents.length}
                hasMore={hasMore}
                height={390}
                loader={
                  loading ? (
                    <Grid className={classes.GridLoader}>
                      <CircularProgress />
                    </Grid>
                  ) : null
                }
              >
                <RACTable
                  renderTableHead={renderTableHead}
                  renderTableContent={renderTableContent}
                  stickyHeader
                />
              </InfiniteScroll>
            )}
          </Card>
        </Grid>
      </Grid>

      <Grid
        container
        alignItems="center"
        className={clsx(classes.customerToolbar, classes.fixedBottom)}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 99,
          background: '#fff',
          borderTop: '1px solid #eee',
          minHeight: 60,
        }}
      >
        <Grid item xs={6}>
          {selectedCount > 0 && (
            <Typography
              variant="body1"
              style={{ paddingLeft: 32 }}
              className={classes.selectedStyle}
            >
              Selected: {selectedCount}
            </Typography>
          )}
        </Grid>
        <Grid
          item
          xs={6}
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingRight: 32,
          }}
        >
          <RACButton
            size="small"
            variant="contained"
            color="primary"
            disabled={isExportDisabled}
            onClick={() => {
              if (hasAccess && panelOpen) {
                setEmailModalOpen(true);
                return;
              }
              if (selectedCount < 1) return;
              handleExportSelected();
            }}
          >
            Export
          </RACButton>
        </Grid>
      </Grid>
    </div>
  );
}
